import { LOWER_FITNESS_EXERCISE_LABELS, type LowerFitnessLabelId } from '@/lib/fitnessLowerExerciseLabels';
import { APPROVED_FITNESS_EXERCISE_LABELS, type ApprovedFitnessLabelId } from '@/lib/fitnessApprovedExerciseLabels';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang, type Lang } from '@/i18n/locale';
import type { FITNESS_PRODUCT_VARIANTS } from '@/lib/fitnessProductVariants';

type ProductMovementId = keyof typeof FITNESS_PRODUCT_VARIANTS;
/** Labels describe the selected product variant without changing stored keys/history. */
export const FITNESS_PRODUCT_EXERCISE_LABELS = {
  "EN": {
    "reverse-crunch": "Reverse Crunch",
    "supine-leg-raise": "Floor Leg Raise",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Bicycle Crunch",
    "cross-body-oblique-crunch": "Cross-body Oblique Crunch",
    "seated-russian-twist": "Seated Russian Twist (Heels Down)",
    "mountain-climber": "Controlled Mountain Climber",
    "kneeling-ab-wheel": "Kneeling Ab Wheel",
    "burpee": "Burpee with Push-up and Jump",
    "close-grip-bench-press": "Close-grip Barbell Bench Press",
    "seated-arnold-press": "Seated Arnold Press (Back Supported)",
    "seated-hip-abduction": "Seated Hip Abduction Machine",
    "chin-up": "Chin-up (Supinated Grip)",
    "flat-dumbbell-bench-press": "Flat Dumbbell Bench Press",
    "standing-dumbbell-shoulder-press": "Standing Dumbbell Shoulder Press"
  },
  "PT-BR": {
    "reverse-crunch": "Abdominal inverso",
    "supine-leg-raise": "Elevação de pernas no chão",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Abdominal bicicleta",
    "cross-body-oblique-crunch": "Abdominal oblíquo cruzado",
    "seated-russian-twist": "Giro sentado sem peso (calcanhares apoiados)",
    "mountain-climber": "Escalador controlado",
    "kneeling-ab-wheel": "Roda abdominal ajoelhado",
    "burpee": "Burpee com flexão e salto",
    "close-grip-bench-press": "Supino com barra e pegada fechada",
    "seated-arnold-press": "Arnold sentado (costas apoiadas)",
    "seated-hip-abduction": "Abdução de quadril na máquina sentada",
    "chin-up": "Barra fixa supinada (chin-up)",
    "flat-dumbbell-bench-press": "Supino com halteres no banco plano",
    "standing-dumbbell-shoulder-press": "Desenvolvimento com halteres em pé"
  },
  "NL": {
    "reverse-crunch": "Omgekeerde crunch",
    "supine-leg-raise": "Beenheffen op de vloer",
    "dead-bug": "Dead bug",
    "bicycle-crunch": "Fietscrunch",
    "cross-body-oblique-crunch": "Kruislingse schuine crunch",
    "seated-russian-twist": "Zittende rompdraai zonder gewicht (hielen op de vloer)",
    "mountain-climber": "Beheerste mountain climber",
    "kneeling-ab-wheel": "Knielende ab-wheel rollout",
    "burpee": "Burpee met push-up en sprong",
    "close-grip-bench-press": "Bankdrukken met halterstang en smalle greep",
    "seated-arnold-press": "Zittende Arnold press (rug ondersteund)",
    "seated-hip-abduction": "Zittende heupabductiemachine",
    "chin-up": "Chin-up (onderhandse greep)",
    "flat-dumbbell-bench-press": "Dumbbell-bankdrukken op vlakke bank",
    "standing-dumbbell-shoulder-press": "Staande schouderdruk met dumbbells"
  },
  "DE": {
    "reverse-crunch": "Umgekehrter Crunch",
    "supine-leg-raise": "Beinheben am Boden",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Fahrrad-Crunch",
    "cross-body-oblique-crunch": "Diagonaler schräger Crunch",
    "seated-russian-twist": "Rumpfdrehung im Sitzen ohne Gewicht (Fersen am Boden)",
    "mountain-climber": "Kontrollierter Mountain Climber",
    "kneeling-ab-wheel": "Ab-Wheel-Rollout im Knien",
    "burpee": "Burpee mit Liegestütz und Sprung",
    "close-grip-bench-press": "Langhantel-Bankdrücken mit engem Griff",
    "seated-arnold-press": "Arnold Press im Sitzen (Rücken abgestützt)",
    "seated-hip-abduction": "Hüftabduktionsmaschine im Sitzen",
    "chin-up": "Chin-up (Untergriff)",
    "flat-dumbbell-bench-press": "Kurzhantel-Bankdrücken auf flacher Bank",
    "standing-dumbbell-shoulder-press": "Schulterdrücken mit Kurzhanteln im Stehen"
  },
  "IT": {
    "reverse-crunch": "Crunch inverso",
    "supine-leg-raise": "Sollevamento gambe a terra",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Crunch bicicletta",
    "cross-body-oblique-crunch": "Crunch obliquo incrociato",
    "seated-russian-twist": "Rotazione da seduti senza peso (talloni a terra)",
    "mountain-climber": "Mountain climber controllato",
    "kneeling-ab-wheel": "Ruota addominale in ginocchio",
    "burpee": "Burpee con piegamento e salto",
    "close-grip-bench-press": "Panca piana con bilanciere a presa stretta",
    "seated-arnold-press": "Arnold press da seduti (schiena sostenuta)",
    "seated-hip-abduction": "Abduzione delle anche alla macchina da seduti",
    "chin-up": "Chin-up (presa supina)",
    "flat-dumbbell-bench-press": "Panca piana con manubri",
    "standing-dumbbell-shoulder-press": "Lento avanti con manubri in piedi"
  },
  "FR": {
    "reverse-crunch": "Crunch inversé",
    "supine-leg-raise": "Relevé de jambes au sol",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Crunch bicyclette",
    "cross-body-oblique-crunch": "Crunch oblique croisé",
    "seated-russian-twist": "Rotation assise sans charge (talons au sol)",
    "mountain-climber": "Mountain climber contrôlé",
    "kneeling-ab-wheel": "Roue abdominale à genoux",
    "burpee": "Burpee avec pompe et saut",
    "close-grip-bench-press": "Développé couché à la barre en prise serrée",
    "seated-arnold-press": "Développé Arnold assis (dos soutenu)",
    "seated-hip-abduction": "Machine d'abduction des hanches assise",
    "chin-up": "Chin-up (prise en supination)",
    "flat-dumbbell-bench-press": "Développé couché aux haltères sur banc plat",
    "standing-dumbbell-shoulder-press": "Développé épaules aux haltères debout"
  },
  "PL": {
    "reverse-crunch": "Odwrotne spięcia brzucha",
    "supine-leg-raise": "Unoszenie nóg w leżeniu na podłodze",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Rowerek brzuszny",
    "cross-body-oblique-crunch": "Skośne spięcia krzyżowe",
    "seated-russian-twist": "Skręty tułowia w siadzie bez ciężaru (pięty oparte)",
    "mountain-climber": "Kontrolowany mountain climber",
    "kneeling-ab-wheel": "Kółko do brzucha w klęku",
    "burpee": "Burpee z pompką i wyskokiem",
    "close-grip-bench-press": "Wyciskanie sztangi na ławce płaskiej wąskim chwytem",
    "seated-arnold-press": "Wyciskanie Arnolda w siadzie (plecy podparte)",
    "seated-hip-abduction": "Odwodzenie bioder na maszynie w siadzie",
    "chin-up": "Podciąganie podchwytem (chin-up)",
    "flat-dumbbell-bench-press": "Wyciskanie hantli na ławce płaskiej",
    "standing-dumbbell-shoulder-press": "Wyciskanie hantli nad głowę w staniu"
  },
  "BG": {
    "reverse-crunch": "Обратни коремни преси",
    "supine-leg-raise": "Повдигане на крака от лег на пода",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Коремни преси велосипед",
    "cross-body-oblique-crunch": "Кръстосани коси коремни преси",
    "seated-russian-twist": "Завъртане от седеж без тежест (пети опрени)",
    "mountain-climber": "Контролиран планински катерач",
    "kneeling-ab-wheel": "Коремно колело от колене",
    "burpee": "Бърпи с лицева опора и скок",
    "close-grip-bench-press": "Изтласкване с щанга от лег с тесен хват",
    "seated-arnold-press": "Арнолд преса от седеж (гръб опрян)",
    "seated-hip-abduction": "Отвеждане на бедрата на машина от седеж",
    "chin-up": "Набиране с подхват (chin-up)",
    "flat-dumbbell-bench-press": "Изтласкване с дъмбели от хоризонтален лег",
    "standing-dumbbell-shoulder-press": "Раменна преса с дъмбели от стоеж"
  },
  "ES": {
    "reverse-crunch": "Crunch inverso",
    "supine-leg-raise": "Elevación de piernas en el suelo",
    "dead-bug": "Dead Bug",
    "bicycle-crunch": "Crunch bicicleta",
    "cross-body-oblique-crunch": "Crunch oblicuo cruzado",
    "seated-russian-twist": "Giro sentado sin peso (talones apoyados)",
    "mountain-climber": "Escalador controlado",
    "kneeling-ab-wheel": "Rueda abdominal de rodillas",
    "burpee": "Burpee con flexión y salto",
    "close-grip-bench-press": "Press de banca plano con barra y agarre cerrado",
    "seated-arnold-press": "Press Arnold sentado (espalda apoyada)",
    "seated-hip-abduction": "Abducción de cadera en máquina sentado",
    "chin-up": "Dominada supina (chin-up)",
    "flat-dumbbell-bench-press": "Press de banca plano con mancuernas",
    "standing-dumbbell-shoulder-press": "Press de hombros con mancuernas de pie"
  }
} satisfies Record<Lang, Record<ProductMovementId, string>>;

/** Literal curated-plan PT labels approved in the product review; no movement aliases. */
export const PT_CURATED_PLAN_EXERCISE_LABELS: Readonly<Record<string, string>> = {
  "Chin-up": "Barra fixa supinada (chin-up)",
  "Ab Wheel": "Roda abdominal",
  "Battle Ropes": "Cordas de batalha",
  "Box Jump": "Salto na caixa",
  "Burpees": "Burpee",
  "Cable Flyes": "Crucifixo no cabo",
  "Cable Row": "Remada no cabo",
  "Clean and Press": "Clean e desenvolvimento",
  "Close-Grip Bench": "Supino com pegada fechada",
  "Deficit Deadlift": "Levantamento terra em déficit",
  "Dumbbell Row": "Remada com halter",
  "Front Squat": "Agachamento frontal",
  "Incline Dumbbell Press": "Supino inclinado com halteres",
  "Kettlebell Swing": "Balanço com kettlebell",
  "Mountain Climbers": "Escalador",
  "Pause Squat": "Agachamento com pausa",
  "Push Press": "Desenvolvimento com impulso das pernas",
  "Sprint Intervals": "Tiros intervalados de corrida",
  "Tricep Pushdown": "Extensão de tríceps na polia",
  "Walking Lunges": "Avanço caminhando",
  "Weighted Pull-up": "Barra fixa pronada com peso adicional"
};

export function localizeFitnessProductExerciseName(value: string, lang?: string): string | null {
  if (typeof value !== 'string') return null;
  const id = getExplicitFitnessMovement(value)?.id;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const dictionary = FITNESS_PRODUCT_EXERCISE_LABELS[language];
  if (id && id in dictionary) return dictionary[id as ProductMovementId];
  const approved = APPROVED_FITNESS_EXERCISE_LABELS[language];
  if (id && id in approved) return approved[id as ApprovedFitnessLabelId];
  const lower = LOWER_FITNESS_EXERCISE_LABELS[language];
  if (id && id in lower) return lower[id as LowerFitnessLabelId];
  return language === 'PT-BR' && Object.prototype.hasOwnProperty.call(PT_CURATED_PLAN_EXERCISE_LABELS, value) ? PT_CURATED_PLAN_EXERCISE_LABELS[value] : null;
}
